import { useState, useEffect, useCallback } from 'react';
import { useApp, useAppState } from '../../../context/AppContext';
import KeycloakAPI from '../../../api/keycloak';
import useErrorHandler from '../../../shared/hooks/useErrorHandler';
import { parseDataBySelect } from '../../../shared/utils/parseDataBySelect';
import { IconButton } from '../../../insfrastructure/ui/button';
import NavigationService from '../../../utils/history';

/**
 * Hook personalizado para manejar usuarios del ecosistema
 * Reemplaza EcosystemUserController
 * 
 * @returns {Object} Objeto con datos y funciones para manejar usuarios del ecosistema
 */
export const useEcosystemUsers = () => {
    const { dispatch, notify } = useApp();
    const ecosystemUsers = useAppState('ecosystem_users');
    const handleError = useErrorHandler();
    
    const [data, setData] = useState(ecosystemUsers || []);
    const [isLoading, setIsLoading] = useState(false);

    // Columnas para la tabla
    const columns = [        
        { key: 'username', label: "Nombre de usuario" },
        { key: 'first_name', label: "Nombre" },
        { key: 'last_name', label: "Apellido" },
        { key: 'email', label: "Correo eléctronico" },
        { key: 'email_verified', label: "Email verificado" },
        { key: 'enabled', label: "Estado" },
        { key: 'balance', label: "Puntos Ñamis" },
        { key: 'actions', label: 'Acciones', style: { width: "max-content" } }
    ];

    // Custom elements para la tabla
    const customElements = {
        points: data => <div>{data?.wallet_points?.data?.balance}</div>,
        email_verified: data => <div className="text-center text-bold">{data.email_verified ? "Confirmado" : "Pendiente"}</div>,
        enabled: data => <div className="text-center text-bold">{data.enabled ? "Activo" : "Inactivo"}</div>,
        actions: (data) => (
            <div style={{ width: 120 }}>
                <IconButton onClick={() => sendEmailVerification(data.email)} tooltip="Reenviar correo de verificación">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22 10.5V15.5C22 19 20 20.5 17 20.5H7C4 20.5 2 19 2 15.5V8.5C2 5 4 3.5 7 3.5H14" stroke="#292D32" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M7 9L10.13 11.5C11.16 12.32 12.85 12.32 13.88 11.5L15.06 10.56" stroke="#292D32" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M19.5 8C20.8807 8 22 6.88071 22 5.5C22 4.11929 20.8807 3 19.5 3C18.1193 3 17 4.11929 17 5.5C17 6.88071 18.1193 8 19.5 8Z" stroke="#292D32" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </IconButton>
                <IconButton onClick={() => sendEmailResetPassword(data.email)} tooltip="Reenviar correo de reseteo de password">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19.79 14.9299C17.73 16.9799 14.78 17.6099 12.19 16.7999L7.48002 21.4999C7.14002 21.8499 6.47002 22.0599 5.99002 21.9899L3.81002 21.6899C3.09002 21.5899 2.42002 20.9099 2.31002 20.1899L2.01002 18.0099C1.94002 17.5299 2.17002 16.8599 2.50002 16.5199L7.20002 11.8199C6.40002 9.21995 7.02002 6.26995 9.08002 4.21995C12.03 1.26995 16.82 1.26995 19.78 4.21995C22.74 7.16995 22.74 11.9799 19.79 14.9299Z" stroke="#292D32" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M6.89001 17.49L9.19001 19.79" stroke="#292D32" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M14.5 11C15.3284 11 16 10.3284 16 9.5C16 8.67157 15.3284 8 14.5 8C13.6716 8 13 8.67157 13 9.5C13 10.3284 13.6716 11 14.5 11Z" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </IconButton>
            </div>
        ),
    };

    // Sincronizar con estado global
    useEffect(() => {
        if (ecosystemUsers) setData(ecosystemUsers);
    }, [ecosystemUsers]);

    /**
     * Obtener todos los usuarios del ecosistema
     */
    const getAll = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await KeycloakAPI.getUsers();
            const responseData = res.data || res;
            const usersList = Array.isArray(responseData) 
                ? responseData.map(e => parseDataBySelect(e, e.name || e.username || 'Sin nombre'))
                : [];
            
            dispatch({ type: 'SET_ECOSYSTEM_USERS', payload: usersList });
            setData(usersList);
            return usersList;
        } catch (error) {
            handleError(error, 'Error al obtener los usuarios del ecosistema');
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [dispatch, handleError]);

    /**
     * Reenviar correo de verificación
     */
    const resendVerification = useCallback(async (email) => {
        try {
            await KeycloakAPI.resendVerification(email);
            notify({ 
                title: "Éxito!", 
                type: "success", 
                text: "Email de verificación enviado" 
            });
        } catch (error) {
            // No rechazar el error, solo loguearlo (comportamiento original)
            handleError(error, 'Error al reenviar el correo de verificación');
        }
    }, [notify, handleError]);

    /**
     * Enviar correo de reseteo de contraseña
     */
    const resetPassword = useCallback(async (email) => {
        try {
            await KeycloakAPI.resetPassword(email);
            notify({ 
                title: "Éxito!", 
                type: "success", 
                text: "Email de reseteo de contraseña enviado" 
            });
        } catch (error) {
            handleError(error, 'Error al enviar el correo de reseteo de contraseña');
            throw error;
        }
    }, [notify, handleError]);

    /**
     * Wrapper para mantener compatibilidad
     */
    const sendEmailVerification = useCallback((email) => {
        resendVerification(email);
    }, [resendVerification]);

    /**
     * Wrapper para mantener compatibilidad
     */
    const sendEmailResetPassword = useCallback((email) => {
        resetPassword(email);
    }, [resetPassword]);

    /**
     * Navegar atrás
     */
    const goBack = useCallback(() => {
        NavigationService.navigateBack();
    }, []);

    return {
        // Datos
        data,
        ecosystemUsers,
        columns,
        customElements,
        
        // Estado
        isLoading,
        
        // Funciones
        getAll,
        resendVerification,
        resetPassword,
        sendEmailVerification,
        sendEmailResetPassword,
        goBack,
        
        // Helpers
        refresh: getAll,
    };
};

export default useEcosystemUsers;

